import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

// ---------------------------------------------------------------------------
// Client-side PDF export for the advisory agreement. Each `.ag-sheet`
// (a designed 8.5x11in page) is rendered to its own PDF page so the output
// matches the on-screen document exactly — no browser print dialog required.
// ---------------------------------------------------------------------------

async function buildContractPdf() {
  const sheets = Array.from(document.querySelectorAll(".ag-sheet"));
  if (sheets.length === 0) throw new Error("No contract pages found to export");

  // Legal pages (8.5 x 14in) so each section sits on its own page and content
  // — signatures, the payment table, party blocks — is never split mid-page.
  const pdf = new jsPDF({ unit: "pt", format: "legal", compress: true });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const FOOTER_BAND = 30; // pt reserved at the bottom of each page for the footer
  const usableH = pageH - FOOTER_BAND;

  let firstPage = true;
  for (let i = 0; i < sheets.length; i++) {
    // eslint-disable-next-line no-await-in-loop
    const canvas = await html2canvas(sheets[i], {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
      windowWidth: sheets[i].scrollWidth,
      // Bake field values cleanly + drop the in-document footer (we stamp an
      // accurate page footer onto every page afterwards).
      onclone: (_doc, clonedSheet) => {
        bakeFields(sheets[i], clonedSheet);
        clonedSheet.querySelectorAll(".ag-sheet-footer").forEach((f) => { f.style.display = "none"; });
      },
    });
    const img = canvas.toDataURL("image/jpeg", 0.95);

    // One section per page: scale to fit the usable area, preserving aspect
    // ratio (never distorted, never broken across pages). Top-aligned and
    // horizontally centered.
    if (!firstPage) pdf.addPage();
    firstPage = false;
    const scale = Math.min(pageW / canvas.width, usableH / canvas.height);
    const w = canvas.width * scale;
    const h = canvas.height * scale;
    const x = (pageW - w) / 2;
    pdf.addImage(img, "JPEG", x, 0, w, h, undefined, "FAST");
  }

  // Stamp an accurate footer (© line + page number) on each page.
  const total = pdf.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    pdf.setPage(p);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.setTextColor(120, 120, 120);
    pdf.text("© OmniSportsGroup, LLC (d/b/a OmniDraftLab) · Pre-Draft Advisory Services Agreement · Tennessee", 36, pageH - 16);
    pdf.text(`Page ${p} of ${total}`, pageW - 36, pageH - 16, { align: "right" });
  }
  return pdf;
}

// Replace every <input>/<textarea> in the cloned sheet with a static div that
// shows the live value, styled to match — so the captured text is never
// clipped by the field's underline. Empty fields render as a blank line.
function bakeFields(originalSheet, clonedSheet) {
  const originals = originalSheet.querySelectorAll("input, textarea");
  const clones = clonedSheet.querySelectorAll("input, textarea");
  clones.forEach((node, idx) => {
    const src = originals[idx];
    if (!src) return;
    const cs = window.getComputedStyle(src);
    const isTextarea = src.tagName === "TEXTAREA";
    const div = clonedSheet.ownerDocument.createElement("div");
    div.textContent = src.value || "";
    div.style.boxSizing = "border-box";
    div.style.display = "block";
    div.style.width = cs.width;
    div.style.minHeight = cs.height;
    div.style.fontFamily = cs.fontFamily;
    div.style.fontSize = cs.fontSize;
    div.style.fontWeight = cs.fontWeight;
    div.style.fontStyle = cs.fontStyle;
    div.style.letterSpacing = cs.letterSpacing;
    div.style.color = cs.color;
    div.style.lineHeight = "1.4";
    div.style.paddingLeft = cs.paddingLeft;
    div.style.paddingRight = cs.paddingRight;
    div.style.paddingTop = "2px";
    div.style.paddingBottom = "7px";
    div.style.borderBottom = `${cs.borderBottomWidth} ${cs.borderBottomStyle === "none" ? "solid" : cs.borderBottomStyle} ${cs.borderBottomColor}`;
    div.style.background = "transparent";
    div.style.whiteSpace = isTextarea ? "pre-wrap" : "nowrap";
    div.style.overflow = "hidden";
    if (node.parentNode) node.parentNode.replaceChild(div, node);
  });
}

export function safeFileName(name) {
  return (name || "Advisory-Agreement")
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export async function downloadContractPdf(athleteName) {
  const pdf = await buildContractPdf();
  pdf.save(`Advisory-Agreement-${safeFileName(athleteName)}.pdf`);
}

// Returns the bare base64 (no data: prefix) for emailing as an attachment.
export async function contractPdfBase64() {
  const pdf = await buildContractPdf();
  const dataUri = pdf.output("datauristring");
  return dataUri.split(",")[1];
}
